import { useEffect, useState } from 'react';
import { Input as RoccoInput } from '@rocco/components/input';
import { Slider } from '@rocco/components/slider/slider';
import type { FilterConfigModalItemPrice } from '@rocco/ui/filter-sort/types/filter-config-modal';
import type { RangeFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/range-filter-state';
import type { FilterModalElementProps } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';
import { formatNumberWithCommas } from '@rocco/utils/format-number';

export const FiltersModalPrice = (
    props: FilterModalElementProps<
        FilterConfigModalItemPrice,
        RangeFilterState
    >,
) => {
    const { filterState, onFilterStateUpdate } = props;
    const [localRange, setLocalRange] = useState([
        filterState.value.min,
        filterState.value.max,
    ]);

    const handleSliderChange = (newRange: [number, number]) => {
        onFilterStateUpdate({
            ...filterState,
            value: {
                min: Math.max(filterState.range.min, newRange[0]),
                max: Math.min(filterState.range.max, newRange[1]),
            },
        });
    };

    // On blur: validate, clamp, normalize, then propagate once
    const applyRangeChange = () => {
        const rawMin = localRange[0] ?? 0;
        const rawMax = localRange[1] ?? 0;

        // Guard against NaN (e.g., user typed just ".")
        const safeMin = isNaN(rawMin) ? filterState.range.min : rawMin;
        const safeMax = isNaN(rawMax) ? filterState.range.max : rawMax;

        // Clamp to allowed range
        const clampedMin = Math.max(
            filterState.range.min,
            Math.min(safeMin, filterState.range.max),
        );
        const clampedMax = Math.max(
            filterState.range.min,
            Math.min(safeMax, filterState.range.max),
        );

        // Normalize min/max order
        const normalizedMin = Math.min(clampedMin, clampedMax);
        const normalizedMax = Math.max(clampedMin, clampedMax);

        setLocalRange([normalizedMin, normalizedMax]);
        onFilterStateUpdate(
            {
                ...filterState,
                value: { min: normalizedMin, max: normalizedMax },
            },
            true,
        );
    };

    useEffect(() => {
        if (
            filterState.value.min !== localRange[0] ||
            filterState.value.max !== localRange[1]
        ) {
            setLocalRange([filterState.value.min, filterState.value.max]);
        }
        // we don't need to trigger this effect when local state is changed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.value.min, filterState.value.max]);

    // Hide when category has no price spread (all products same price)
    // Uses range (server aggregation) not value (user input) to avoid
    // unmounting when user types equal values in both inputs
    if (filterState.range.min === filterState.range.max) {
        return null;
    }

    return (
        <div className="flex flex-col flex-wrap gap-2 pt-2">
            <div className="px-3 pb-2 pt-6">
                <Slider
                    value={localRange}
                    min={filterState.range.min}
                    max={filterState.range.max}
                    onValueChange={setLocalRange}
                    onValueCommit={handleSliderChange}
                    step={1}
                />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                <div className="w-32">
                    <RoccoInput
                        type="text"
                        inputMode="numeric"
                        prefix="$"
                        color="blackOutline"
                        value={formatNumberWithCommas(
                            (localRange[0] ?? 0) / filterState.rangeDivisor,
                        )}
                        onChange={(e) => {
                            const parsed =
                                Number(
                                    e.currentTarget.value.replace(
                                        /[^0-9.]/g,
                                        '',
                                    ),
                                ) || 0;
                            setLocalRange([
                                Math.round(parsed * filterState.rangeDivisor),
                                localRange[1] ?? 0,
                            ]);
                        }}
                        onBlur={applyRangeChange}
                    />
                </div>

                <div className="flex items-center justify-center">
                    <div className="h-px w-1.5 bg-gray-900" />
                </div>

                <div className="w-32">
                    <RoccoInput
                        type="text"
                        inputMode="numeric"
                        prefix="$"
                        color="blackOutline"
                        value={formatNumberWithCommas(
                            (localRange[1] ?? 0) / filterState.rangeDivisor,
                        )}
                        onChange={(e) => {
                            const parsed =
                                Number(
                                    e.currentTarget.value.replace(
                                        /[^0-9.]/g,
                                        '',
                                    ),
                                ) || 0;
                            setLocalRange([
                                localRange[0] ?? 0,
                                Math.round(parsed * filterState.rangeDivisor),
                            ]);
                        }}
                        onBlur={applyRangeChange}
                    />
                </div>
            </div>
        </div>
    );
};
